import {SelectionFlowEnum} from 'presentation/constants';
import {HandleSelectPlanProps} from 'presentation/pages/others/selection-flow/components/PlanCard/types';
import {useSelection} from 'presentation/pages/others/selection-flow/hooks';
import {useGetBeneficiaries} from 'presentation/pages/others/selection-flow/queries/useGetBeneficiaries';
import {useGetPlans} from 'presentation/pages/others/selection-flow/queries/useGetPlans';
import {useSelectedBeneficiaryStore} from 'presentation/pages/others/selection-flow/store/useSelectedBeneficiaryStore';
import {useSelectedPlanStore} from 'presentation/pages/others/selection-flow/store/useSelectedPlanStore';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

export const usePlanSelection = () => {
	const setSelectedPlan = useSelectedPlanStore(state => state.setSelectedPlan);
	const [planCardSelectedState, setPlanCardSelectedState] = useState<string>('');
	const setSelectedBeneficiary = useSelectedBeneficiaryStore(
		state => state.setSelectedBeneficiary,
	);
	const setAccountData = useAccountDataStore(state => state.setAccountData);

	const {confirmSelection, isSubmitting} = useSelection();
	const navigate = useNavigate();
	const {state} = useLocation();
	const isCommingFromLoginPaths = Boolean(state?.isCommingFromLoginPaths);

	const isInInitialLoginFlow = Boolean(state?.isLogin);

	const {data: plansList, isLoading: isPlansListLoading} = useGetPlans();

	const isUniquePlan = plansList?.length === 1;

	const uniquePlan = plansList && isUniquePlan ? plansList?.[0] : undefined;

	const {data: beneficiariesList, isLoading: beneficiariesListIsLoading} = useGetBeneficiaries({
		nuContrato: uniquePlan?.nuContrato,
	});

	const isUniqueBeneficiaryInThisPlan = beneficiariesList?.length === 1;

	const handleSelectPlan = async ({
		plan,
		beneficiary: uniqueBeneficiary,
	}: HandleSelectPlanProps) => {
		if (isInInitialLoginFlow && uniqueBeneficiary) {
			setSelectedPlan({
				nmPlano: uniqueBeneficiary.nmPlano,
				nmUsuario: uniqueBeneficiary.nmUsuario,
				nuContrato: uniqueBeneficiary.nuContrato,
				nmUsuarioC: uniqueBeneficiary.nmUsuarioC,
				tipoPlanoC: uniqueBeneficiary.tipoPlanoC,
				nmSocialUsuarioC: uniqueBeneficiary.nmSocialUsuarioC,
				nmSocialUsuario: null,
				tipoBeneficiarioLogado: uniqueBeneficiary?.tipoUsuarioC,
				statusBeneficiarioLogado: uniqueBeneficiary?.situacao || null,
				dataCancelamentoBeneficiarioLogado: uniqueBeneficiary?.dataCancelamento || null,
				dtCancelamento: uniqueBeneficiary?.dataCancelamento || null,
				acessoSuspenso: uniqueBeneficiary?.acessoSuspenso || null,
				cdPlano: plan?.cdPlano,
			});
			const planUniqueId = `${uniqueBeneficiary?.nuContrato}-${uniqueBeneficiary?.tipoUsuarioC}`;
			setPlanCardSelectedState(planUniqueId);
			setSelectedBeneficiary(uniqueBeneficiary);
			await confirmSelection({beneficiary: uniqueBeneficiary});
			setAccountData({
				beneficiariesLength: 1,
				planBeneficiaries: beneficiariesList ?? [],
			});

			return;
		}

		const planUniqueId = `${plan?.nuContrato}-${plan?.tipoBeneficiarioLogado}`;
		setPlanCardSelectedState(planUniqueId);
		setSelectedPlan(plan);
		setTimeout(() => {
			navigate(SelectionFlowEnum.BENEFICIARY_SELECTION, {state});
		}, 400);
	};

	const isUniquePlanAndBeneficiary =
		isInInitialLoginFlow &&
		!isPlansListLoading &&
		isUniquePlan &&
		!beneficiariesListIsLoading &&
		isUniqueBeneficiaryInThisPlan;

	const shouldAutoSelectUniquePlanAndBeneficiary =
		isUniquePlanAndBeneficiary && !isCommingFromLoginPaths;

	const shouldAutoSelectUniquePlan =
		isInInitialLoginFlow &&
		!isPlansListLoading &&
		isUniquePlan &&
		!beneficiariesListIsLoading &&
		!isUniqueBeneficiaryInThisPlan &&
		!isCommingFromLoginPaths;

	const showLoadingScreen =
		(isInInitialLoginFlow && (isSubmitting || (isUniquePlan && !isCommingFromLoginPaths))) ||
		beneficiariesListIsLoading ||
		isPlansListLoading;

	const shouldLogout = !isPlansListLoading && (!plansList || plansList?.length === 0);

	return {
		planCardSelectedState,
		handleSelectPlan,
		shouldAutoSelectUniquePlanAndBeneficiary,
		shouldAutoSelectUniquePlan,
		showLoadingScreen,
		shouldLogout,
		plansList,
		uniquePlan,
		beneficiariesList,
		isInInitialLoginFlow,
	};
};
